import type { TournamentRegistrationStatus } from "@/services/tournament-registration.service";
import { getTeamPath } from "@/utils/team.utils";
import {
  BR_TIMEZONE,
  formatBrDateTime,
  splitBrEventDate,
} from "@/utils/br-datetime";

export type TournamentShareRef =
  | string
  | {
      id: string;
      shortCode?: string | null;
    };

export type RegistrationWindowStatus = "open" | "upcoming" | "closed" | "unscheduled";

export function getRegistrationWindowStatus(
  openAt: string | null | undefined,
  closeAt: string | null | undefined,
  now = new Date(),
): RegistrationWindowStatus {
  if (!openAt?.trim() && !closeAt?.trim()) return "unscheduled";

  let openMs: number | undefined;
  let closeMs: number | undefined;
  try {
    if (openAt?.trim()) {
      const d = new Date(openAt).getTime();
      openMs = Number.isNaN(d) ? undefined : d;
    }
    if (closeAt?.trim()) {
      const d = new Date(closeAt).getTime();
      closeMs = Number.isNaN(d) ? undefined : d;
    }
  } catch {
    return "unscheduled";
  }

  const t = now.getTime();
  if (openMs != null && t < openMs) return "upcoming";
  if (closeMs != null && t > closeMs) return "closed";
  return "open";
}

type TournamentOrganizerRef = { id: string; slug?: string | null | undefined };

export type TournamentRegistrationAvailabilityRef = {
  status?: string;
  registrationOpenAt?: string | null;
  registrationCloseAt?: string | null;
  registrationType?: "TEAM" | "INDIVIDUAL" | "BOTH";
  maxTeamRegistrations?: number | null;
  maxIndividualRegistrations?: number | null;
  playersPerTeam?: number | null;
  teamRegistrationsCount?: number;
  individualRegistrationsCount?: number;
  bothIndividualSlotLimit?: number | null;
  registrationsCount?: number;
};

/** Vagas individuais restantes no modo BOTH (equipes têm prioridade). */
export function computeBothIndividualSlotLimit(
  maxTeamRegistrations: number | null | undefined,
  playersPerTeam: number | null | undefined,
  teamRegistrationsCount: number,
): number | null {
  if (
    maxTeamRegistrations == null ||
    playersPerTeam == null ||
    maxTeamRegistrations <= 0 ||
    playersPerTeam <= 0
  ) {
    return null;
  }
  const remainingTeamSlots = Math.max(0, maxTeamRegistrations - teamRegistrationsCount);
  return remainingTeamSlots * playersPerTeam;
}

function resolveBothIndividualSlotLimit(
  tournament: TournamentRegistrationAvailabilityRef,
): number | null {
  if (tournament.bothIndividualSlotLimit != null) {
    return tournament.bothIndividualSlotLimit;
  }
  return computeBothIndividualSlotLimit(
    tournament.maxTeamRegistrations,
    tournament.playersPerTeam,
    tournament.teamRegistrationsCount ?? 0,
  );
}

export type TournamentNavigationRef =
  | string
  | ({
      id: string;
      shortCode?: string | null;
      organizerTeam?: TournamentOrganizerRef | null;
      team?: TournamentOrganizerRef | null;
    } & TournamentRegistrationAvailabilityRef);

export function isTournamentRegistrationFull(
  tournament: TournamentRegistrationAvailabilityRef,
  registrationKind?: "TEAM" | "INDIVIDUAL",
): boolean {
  if (tournament.registrationType === "BOTH" && registrationKind) {
    const current =
      registrationKind === "TEAM"
        ? (tournament.teamRegistrationsCount ?? 0)
        : (tournament.individualRegistrationsCount ?? 0);
    const maxSlots =
      registrationKind === "TEAM"
        ? tournament.maxTeamRegistrations
        : resolveBothIndividualSlotLimit(tournament) ??
          tournament.maxIndividualRegistrations;
    return maxSlots != null && maxSlots > 0 && current >= maxSlots;
  }
  const maxSlots =
    tournament.registrationType === "TEAM"
      ? tournament.maxTeamRegistrations
      : tournament.maxIndividualRegistrations;
  const current = tournament.registrationsCount ?? 0;
  return maxSlots != null && maxSlots > 0 && current >= maxSlots;
}

export function isTournamentStarted(status?: string | null): boolean {
  return status != null && status !== "DRAFT";
}

/** Inscrições abertas agora: torneio em DRAFT, janela ativa e vagas disponíveis. */
export function canOpenTournamentRegistration(
  tournament: TournamentRegistrationAvailabilityRef,
  now = new Date(),
): boolean {
  if (isTournamentStarted(tournament.status)) {
    return false;
  }

  const windowStatus = getRegistrationWindowStatus(
    tournament.registrationOpenAt,
    tournament.registrationCloseAt,
    now,
  );
  if (windowStatus === "closed" || windowStatus === "upcoming") {
    return false;
  }

  return !isTournamentRegistrationFull(tournament);
}

/** Card/home: detalhes só se inscrito confirmado, lotado ou torneio já iniciado. */
export function shouldNavigateToTournamentDetail(
  tournament: TournamentRegistrationAvailabilityRef,
  userRegistrationStatus?: TournamentRegistrationStatus | null,
): boolean {
  if (isConfirmedTournamentRegistration(userRegistrationStatus)) {
    return true;
  }
  if (isTournamentStarted(tournament.status)) {
    return true;
  }
  return isTournamentRegistrationFull(tournament);
}

export function isConfirmedTournamentRegistration(
  status?: TournamentRegistrationStatus | null,
): boolean {
  return status === "CONFIRMED";
}

function resolveOrganizerTeam(tournament: TournamentNavigationRef): TournamentOrganizerRef | null {
  if (typeof tournament === "string") return null;
  return tournament.organizerTeam ?? tournament.team ?? null;
}

export function getTournamentDetailPath(tournament: TournamentNavigationRef): string {
  const team = resolveOrganizerTeam(tournament);
  const id = typeof tournament === "string" ? tournament : tournament.id;
  if (team) return getTournamentOrganizerTeamDetailPath(id, team);
  return `/tournaments/${id}`;
}

/** Destino ao explorar um torneio (home, cards). Detalhes só se inscrito, lotado ou iniciado. */
export function getTournamentExplorePath(
  tournament: TournamentNavigationRef,
  options?: {
    userRegistrationStatus?: TournamentRegistrationStatus | null;
  },
): string {
  const status = options?.userRegistrationStatus;
  if (status === "PENDING_PAYMENT") {
    return getTournamentRegistrationPath(tournament, "checkout");
  }
  if (
    typeof tournament !== "string" &&
    shouldNavigateToTournamentDetail(tournament, status)
  ) {
    return getTournamentDetailPath(tournament);
  }

  return getTournamentRegistrationPath(tournament);
}

export function shouldRedirectRegistrationToDetail(
  tournament: TournamentRegistrationAvailabilityRef,
  userRegistrationStatus?: TournamentRegistrationStatus | null,
): boolean {
  if (userRegistrationStatus === "PENDING_PAYMENT") {
    return false;
  }

  return shouldNavigateToTournamentDetail(tournament, userRegistrationStatus);
}

/** Tela do torneio no time organizador (chaves, jogos, classificação). */
export function getTournamentOrganizerTeamDetailPath(
  tournamentId: string,
  organizerTeam: { id: string; slug?: string | null | undefined },
): string {
  return `${getTeamPath({ id: organizerTeam.id, slug: organizerTeam.slug ?? undefined }, "tournaments")}/${tournamentId}`;
}

/** Formulário de edição dos dados do torneio (organizador autenticado). */
export function getTournamentEditPath(tournamentId: string): string {
  return `/tournaments/${tournamentId}/edit`;
}

/**
 * Path público do torneio para inscrição/checkout (link curto /t/:code quando houver código).
 * A raiz `/tournaments/:id` redireciona para a tela de detalhes; inscrição fica em `/register`.
 */
export function getTournamentRegistrationPath(
  tournament: TournamentShareRef,
  suffix: "register" | "checkout" | "payment/pix" | "success" = "register",
): string {
  if (typeof tournament === "string") {
    return `/tournaments/${tournament}/${suffix}`;
  }
  const tail = `/${suffix}`;
  if (tournament.shortCode) {
    return `/t/${tournament.shortCode}${tail}`;
  }
  return `/tournaments/${tournament.id}${tail}`;
}

/** Rótulo do formato (cards e detalhes). */
export function formatTournamentFormatLabel(
  classifyFormat: string | null | undefined,
  groupSize?: number | null,
): string {
  if (classifyFormat === "ROUND_ROBIN") return "Pontos corridos";
  if (classifyFormat === "GROUPS") {
    return groupSize != null && groupSize > 0
      ? `Fase de grupos (${groupSize} por grupo)`
      : "Fase de grupos";
  }
  return "Formato a definir";
}

/** Linha secundária do card de torneio (evita "0 partidas · …" antes da chave). */
export function formatTournamentCardMeta(
  matchesCount: number,
  classifyFormat: string | null | undefined,
  groupSize?: number | null,
): string {
  const format = formatTournamentFormatLabel(classifyFormat, groupSize);
  if (matchesCount <= 0) return format;
  const partidas =
    matchesCount === 1 ? "1 partida" : `${matchesCount} partidas`;
  return `${partidas} · ${format}`;
}

/** Contagem de inscrições com plural correto em pt-BR. */
export function formatTournamentRegistrationProgress(
  current: number,
  isTeamRegistration: boolean,
  maxSlots?: number | null,
): string {
  const unit =
    current === 1
      ? isTeamRegistration
        ? "equipe inscrita"
        : "inscrição confirmada"
      : isTeamRegistration
        ? "equipes inscritas"
        : "inscrições confirmadas";

  if (maxSlots != null && maxSlots > 0) {
    return `${current}/${maxSlots} ${unit}`;
  }
  return `${current} ${unit}`;
}

export function formatTournamentStartsAt(iso: string | null | undefined) {
  if (!iso) return null;
  try {
    return formatBrDateTime(iso);
  } catch {
    return null;
  }
}

export function splitTournamentEventDate(iso: string | null | undefined) {
  return splitBrEventDate(iso);
}

export function formatTournamentPeriodLabel(
  open: string | null | undefined,
  close: string | null | undefined,
) {
  if (!open && !close) return null;
  const fmt = (s: string) =>
    new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: BR_TIMEZONE,
    }).format(new Date(s));
  try {
    if (open && close) return `${fmt(open)} — ${fmt(close)}`;
    if (open) return `A partir de ${fmt(open)}`;
    if (close) return `Até ${fmt(close)}`;
  } catch {
    return null;
  }
  return null;
}
